import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Box, Link, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import request from 'api'
import { getConsentApi } from 'pages/policies/api'
import { Consent, VIEW_TYPE, buildPolicySearch } from 'pages/policies/utils'
import Detail from 'pages/policies/views/detail'
import List from 'pages/policies/views/list'
import { convertCountryCode2to3, countryCode } from 'utils/i18n'

import legacyPdps from './assets/legacy-kor-pdps.json'
import { useDateTime, useMappedTranslation } from '../../../../hooks'

export const KorPrivacyNoticesList = () => {
  const { versionId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const view_type = versionId ? VIEW_TYPE.DETAIL : VIEW_TYPE.LIST
  const { state } = location
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const policySearch = buildPolicySearch(searchParams)
  const effectiveCountryCode = paramCountryCode ? convertCountryCode2to3(paramCountryCode) : countryCode
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [consents, setConsents] = useState<Consent[]>()
  const [targetConsent, setTargetConsent] = useState<Consent>()
  const [detail, setDetail] = useState<string>('<div/>')
  const theme = useTheme()
  const dateTime = useDateTime()
  const currentVersion = location.pathname.includes('legacies') ? 'OLD' : 'NEW'
  const t = useMappedTranslation()

  const getConsent = () => {
    if (view_type === VIEW_TYPE.DETAIL && state?.consent !== undefined) {
      setTargetConsent(state.consent)
      return Promise.resolve([state.consent])
    }

    if (currentVersion === 'OLD') {
      const data = legacyPdps as Consent[]
      const sortedData = data.sort((a, b) => (b.publish || 0) - (a.publish || 0))
      setConsents(sortedData)
      if (view_type === VIEW_TYPE.DETAIL) {
        const target = sortedData.find((item) => item.id === versionId)
        setTargetConsent(target)
        return Promise.resolve([target])
      }
      return Promise.resolve(sortedData)
    }

    return getConsentApi('PDP', false, '/v1/consent/registered')
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        } else {
          const data = res.data as Consent[]
          const sortedData = data.sort((a, b) => (b.publish || 0) - (a.publish || 0))
          setConsents(sortedData)
          return sortedData
        }
      })
      .then((data) => {
        if (view_type === VIEW_TYPE.DETAIL) {
          const target = data.find((item) => item.version === versionId)
          setTargetConsent(target)
          return [target]
        }
      })
  }

  const handleButton = (item) => {
    if (item === 'old') {
      setIsLoading(true)
      navigate(`/policies/privacy-notices/legacies${policySearch}`)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (item === 'new') {
      setIsLoading(true)
      navigate(`/policies/privacy-notices${policySearch}`)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      if (currentVersion === 'NEW') {
        return navigate(`/policies/privacy-notices/${item.version}${policySearch}`, {
          state: { consent: item }
        })
      } else {
        return navigate(`/policies/privacy-notices/legacies/${item.version}${policySearch}`, {
          state: { consent: item }
        })
      }
    }
  }

  const renderAction = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          pt: { xs: '52px', md: '72px' }
        }}
      >
        <Typography
          className='off'
          fontSize='13px'
          lineHeight='16px'
          fontWeight='700'
          color={theme.palette.mode === 'dark' ? '#999999' : '#636363'}
        >
          {t('iam.01.Common.Txt.Effective.date', { 0: dateTime(Number(state?.consent?.publish), { format: 'withoutTime' }) })}
        </Typography>
        <Link
          //FIXME
          to={`${currentVersion === 'NEW' ? '/policies/privacy-notices' : '/policies/privacy-notices/legacies'}${policySearch}`}
          component={RouterLink}
          sx={{
            mt: '12px',
            padding: '7px 12px',
            fontSize: '13px',
            lineHeight: '19px',
            minHeight: '0',
            textTransform: 'none',
            color: 'text.primary',
            textDecoration: 'none',
            border: `0.5px solid #636363`,
            borderRadius: '24px',
            textAlign: 'center',
            minWidth: '152px',
            display: 'block',
            height: '33px'
          }}
          data-testid='Link6DB23FDA'
        >
          {t('iam.01.Common.Notice.detail.list.01')}
        </Link>
      </Box>
    )
  }

  useEffect(() => {
    setIsLoading(true)
    setDetail('<div/>')
    if (effectiveCountryCode !== 'KOR') {
      navigate(`/error/404?url=${location.pathname}`, { replace: true })
    }

    getConsent()
      .then((data) => {
        if (view_type === VIEW_TYPE.DETAIL) {
          return request.get(data[0].uri).then((res) => {
            if (res.status !== 200) {
              throw new Error('Failed getConsentBody')
            }

            const document = new DOMParser().parseFromString(res.data, 'text/html')
            const body = document.getElementsByTagName('body').item(0)?.innerHTML
            if (body !== undefined) {
              setDetail(body)
            }
          })
        }
      })
      .catch(() => {
        setConsents([])
        setTargetConsent(undefined)
        setDetail('<div>Failed to load</div>')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [versionId, currentVersion, effectiveCountryCode, paramCountryCode, paramLocale])

  return (
    <Box>
      {view_type === VIEW_TYPE.LIST ? (
        <List
          title={(consents && consents[0]?.title) || ''}
          consents={consents}
          isLoading={isLoading}
          handleListButton={handleButton}
          isPublishTitle
          showOldButton={currentVersion === 'NEW'}
          showNewButton={currentVersion === 'OLD'}
        />
      ) : (
        <Detail
          title={(targetConsent && targetConsent.title) || ''}
          isLoading={isLoading}
          detail={detail}
          action={renderAction()}
        />
      )}
    </Box>
  )
}
